import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import styles from "./VirtualInfiniteScroll.module.css";
import type {
  VirtualInfiniteScrollProps,
  VirtualInfiniteScrollRef,
  KeyboardNavigationState,
} from "./types";

const VirtualInfiniteScroll = forwardRef<
  VirtualInfiniteScrollRef,
  VirtualInfiniteScrollProps<unknown>
>(
  (
    {
      items,
      isLoading,
      hasNextPage,
      loadMore,
      direction = "vertical",
      itemsPerRow = 1,
      estimatedItemSize,
      renderItem,
      renderSkeleton,
      overscan = 5,
      onItemSelect,
      height,
      width = "100%",
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      enableKeyboardNavigation = true,
      skeletonCount = 8,
    },
    ref
  ) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const [keyboardState, setKeyboardState] = useState<KeyboardNavigationState>(
      {
        focusedIndex: -1,
        isNavigating: false,
      }
    );

    // Calculate total items including skeleton placeholders
    const totalItems = useMemo(() => {
      if (isLoading && items.length === 0) {
        return skeletonCount;
      }
      return items.length + (hasNextPage ? skeletonCount : 0);
    }, [items.length, isLoading, hasNextPage, skeletonCount]);

    // For grid layout, calculate the number of rows instead of individual items
    const virtualCount = useMemo(() => {
      if (itemsPerRow <= 1) return totalItems;
      return Math.ceil(totalItems / itemsPerRow);
    }, [totalItems, itemsPerRow]);

    // Configure virtualizer
    const virtualizer = useVirtualizer({
      count: virtualCount,
      getScrollElement: () => parentRef.current,
      estimateSize: () => estimatedItemSize,
      overscan,
      horizontal: direction === "horizontal",
    });

    // Get virtual items
    const virtualItems = virtualizer.getVirtualItems();

    // Intersection Observer for infinite loading
    const observerRef = useRef<IntersectionObserver | null>(null);

    // Create observer once
    useEffect(() => {
      if (!hasNextPage) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && !isLoading) {
            loadMore();
          }
        },
        {
          threshold: 0.1,
          rootMargin: "100px",
        }
      );

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, [hasNextPage, isLoading, loadMore]);

    // Callback ref that automatically updates the observed element
    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (observerRef.current) {
          // Disconnect from previous observations
          observerRef.current.disconnect();
        }

        if (node && observerRef.current && hasNextPage && !isLoading) {
          // Start observing the new last item
          observerRef.current.observe(node);
        }
      },
      [hasNextPage, isLoading]
    );

    // Keyboard navigation handlers
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!enableKeyboardNavigation || items.length === 0) return;

        const { key } = event;
        const isVertical = direction === "vertical";
        const maxIndex = items.length - 1;

        let newFocusedIndex = keyboardState.focusedIndex;

        switch (key) {
          case "ArrowDown":
            if (isVertical) {
              event.preventDefault();
              newFocusedIndex = Math.min(
                keyboardState.focusedIndex + itemsPerRow,
                maxIndex
              );
            }
            break;
          case "ArrowUp":
            if (isVertical) {
              event.preventDefault();
              newFocusedIndex = Math.max(
                keyboardState.focusedIndex - itemsPerRow,
                0
              );
            }
            break;
          case "ArrowRight":
            // Only handle right arrow in grid layout (itemsPerRow > 1) or horizontal layout
            if (itemsPerRow > 1 || !isVertical) {
              event.preventDefault();
              // In grid layout, don't move past the current row
              if (itemsPerRow > 1 && isVertical) {
                const currentRow = Math.floor(
                  keyboardState.focusedIndex / itemsPerRow
                );
                const nextIndex = keyboardState.focusedIndex + 1;
                const nextRow = Math.floor(nextIndex / itemsPerRow);
                // Only move right if we stay in the same row and don't exceed maxIndex
                if (nextRow === currentRow && nextIndex <= maxIndex) {
                  newFocusedIndex = nextIndex;
                }
              } else {
                newFocusedIndex = Math.min(
                  keyboardState.focusedIndex + 1,
                  maxIndex
                );
              }
            }
            break;
          case "ArrowLeft":
            // Only handle left arrow in grid layout (itemsPerRow > 1) or horizontal layout
            if (itemsPerRow > 1 || !isVertical) {
              event.preventDefault();
              // In grid layout, don't move past the current row
              if (itemsPerRow > 1 && isVertical) {
                const currentRow = Math.floor(
                  keyboardState.focusedIndex / itemsPerRow
                );
                const prevIndex = keyboardState.focusedIndex - 1;
                const prevRow = Math.floor(prevIndex / itemsPerRow);
                // Only move left if we stay in the same row and don't go below 0
                if (prevRow === currentRow && prevIndex >= 0) {
                  newFocusedIndex = prevIndex;
                }
              } else {
                newFocusedIndex = Math.max(keyboardState.focusedIndex - 1, 0);
              }
            }
            break;
          case "Home":
            event.preventDefault();
            newFocusedIndex = 0;
            break;
          case "End":
            event.preventDefault();
            newFocusedIndex = maxIndex;
            break;
          case "Enter":
          case " ":
            if (
              keyboardState.focusedIndex >= 0 &&
              keyboardState.focusedIndex <= maxIndex
            ) {
              event.preventDefault();
              const item = items[keyboardState.focusedIndex];
              onItemSelect?.(item, keyboardState.focusedIndex);
            }
            break;
          case "Escape":
            event.preventDefault();
            setKeyboardState({ focusedIndex: -1, isNavigating: false });
            return;
        }

        if (newFocusedIndex !== keyboardState.focusedIndex) {
          setKeyboardState({
            focusedIndex: newFocusedIndex,
            isNavigating: true,
          });

          // Calculate the correct virtual row index for grid layouts
          const virtualRowIndex =
            itemsPerRow <= 1
              ? newFocusedIndex
              : Math.floor(newFocusedIndex / itemsPerRow);

          // Scroll to the focused item's row
          virtualizer.scrollToIndex(virtualRowIndex, {
            align: "center",
            behavior: "smooth",
          });
        }
      },
      [
        enableKeyboardNavigation,
        items,
        direction,
        itemsPerRow,
        keyboardState.focusedIndex,
        onItemSelect,
        virtualizer,
      ]
    );

    // Attach keyboard event listeners
    useEffect(() => {
      if (!enableKeyboardNavigation) return;

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [enableKeyboardNavigation, handleKeyDown]);

    // Imperative handle for ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToIndex: (index: number, align = "auto") => {
          // Convert item index to virtual row index for grid layouts
          const virtualRowIndex =
            itemsPerRow <= 1 ? index : Math.floor(index / itemsPerRow);
          virtualizer.scrollToIndex(virtualRowIndex, {
            align,
            behavior: "smooth",
          });
        },
        scrollToTop: () => {
          virtualizer.scrollToIndex(0, { align: "start", behavior: "smooth" });
        },
        getFocusedIndex: () => keyboardState.focusedIndex,
        setFocusedIndex: (index: number) => {
          setKeyboardState({ focusedIndex: index, isNavigating: true });
          // Also scroll to the item
          const virtualRowIndex =
            itemsPerRow <= 1 ? index : Math.floor(index / itemsPerRow);
          virtualizer.scrollToIndex(virtualRowIndex, {
            align: "center",
            behavior: "smooth",
          });
        },
      }),
      [itemsPerRow, virtualizer, keyboardState.focusedIndex, setKeyboardState]
    );

    // Render virtual row (which may contain multiple items for grid layout)
    const renderVirtualItem = useCallback(
      (virtualItem: VirtualItem) => {
        const rowIndex = virtualItem.index;

        if (itemsPerRow <= 1) {
          // Single column layout - same as before
          const index = rowIndex;
          const isSkeletonItem = index >= items.length;
          const isFocused =
            enableKeyboardNavigation && keyboardState.focusedIndex === index;

          if (isSkeletonItem) {
            return (
              <div
                key={`skeleton-${index}`}
                style={{
                  position: "absolute",
                  top: direction === "vertical" ? 0 : undefined,
                  left: direction === "horizontal" ? 0 : undefined,
                  width:
                    direction === "vertical" ? "100%" : `${virtualItem.size}px`,
                  height:
                    direction === "horizontal"
                      ? "100%"
                      : `${virtualItem.size}px`,
                  transform:
                    direction === "vertical"
                      ? `translateY(${virtualItem.start}px)`
                      : `translateX(${virtualItem.start}px)`,
                }}
                className={styles.virtualItem}
              >
                {renderSkeleton()}
              </div>
            );
          }

          const item = items[index];
          if (!item) return null;

          return (
            <div
              key={virtualItem.key}
              style={{
                position: "absolute",
                top: direction === "vertical" ? 0 : undefined,
                left: direction === "horizontal" ? 0 : undefined,
                width:
                  direction === "vertical" ? "100%" : `${virtualItem.size}px`,
                height:
                  direction === "horizontal" ? "100%" : `${virtualItem.size}px`,
                transform:
                  direction === "vertical"
                    ? `translateY(${virtualItem.start}px)`
                    : `translateX(${virtualItem.start}px)`,
              }}
              className={`${styles.virtualItem} ${
                isFocused ? styles.focused : ""
              }`}
              onClick={() => onItemSelect?.(item, index)}
              role={enableKeyboardNavigation ? "button" : undefined}
              tabIndex={enableKeyboardNavigation && isFocused ? 0 : -1}
              aria-selected={isFocused}
              ref={index === items.length - 1 ? lastItemRef : undefined}
            >
              {renderItem(item, index)}
            </div>
          );
        }

        // Grid layout - render multiple items per row
        const startIndex = rowIndex * itemsPerRow;
        const rowItems = [];

        for (let i = 0; i < itemsPerRow; i++) {
          const itemIndex = startIndex + i;
          const isSkeletonItem = itemIndex >= items.length;
          const isFocused =
            enableKeyboardNavigation &&
            keyboardState.focusedIndex === itemIndex;

          if (itemIndex < totalItems) {
            if (isSkeletonItem) {
              rowItems.push(
                <div key={`skeleton-${itemIndex}`} className={styles.gridItem}>
                  {renderSkeleton()}
                </div>
              );
            } else {
              const item = items[itemIndex];
              if (item) {
                rowItems.push(
                  <div
                    key={`item-${itemIndex}`}
                    className={`${styles.gridItem} ${
                      isFocused ? styles.focused : ""
                    }`}
                    onClick={() => onItemSelect?.(item, itemIndex)}
                    role={enableKeyboardNavigation ? "button" : undefined}
                    tabIndex={enableKeyboardNavigation && isFocused ? 0 : -1}
                    aria-selected={isFocused}
                    ref={
                      itemIndex === items.length - 1 ? lastItemRef : undefined
                    }
                  >
                    {renderItem(item, itemIndex)}
                  </div>
                );
              } else {
                // Add empty grid item to maintain grid structure
                rowItems.push(
                  <div key={`empty-${itemIndex}`} className={styles.gridItem} />
                );
              }
            }
          } else {
            // Add empty grid item to maintain grid structure for partial rows
            rowItems.push(
              <div key={`empty-${itemIndex}`} className={styles.gridItem} />
            );
          }
        }

        return (
          <div
            key={`row-${rowIndex}`}
            style={{
              position: "absolute",
              top: direction === "vertical" ? 0 : undefined,
              left: direction === "horizontal" ? 0 : undefined,
              width:
                direction === "vertical" ? "100%" : `${virtualItem.size}px`,
              height:
                direction === "horizontal" ? "100%" : `${virtualItem.size}px`,
              transform:
                direction === "vertical"
                  ? `translateY(${virtualItem.start}px)`
                  : `translateX(${virtualItem.start}px)`,
              display: "flex",
              gap: "8px",
            }}
            className={styles.virtualItem}
          >
            {rowItems}
          </div>
        );
      },
      [
        items,
        totalItems,
        itemsPerRow,
        direction,
        renderItem,
        renderSkeleton,
        onItemSelect,
        enableKeyboardNavigation,
        keyboardState.focusedIndex,
        lastItemRef,
      ]
    );

    // Container classes based on configuration
    const containerClasses = useMemo(() => {
      const classes = [styles.container];

      if (direction === "horizontal") {
        classes.push(styles.horizontal);
      } else {
        classes.push(styles.vertical);
      }

      if (itemsPerRow > 1) {
        classes.push(styles.grid);
      }

      return classes.join(" ");
    }, [direction, itemsPerRow]);

    return (
      <div
        ref={parentRef}
        className={containerClasses}
        style={{
          height,
          width,
          overflow: "auto",
        }}
        role="list"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        tabIndex={enableKeyboardNavigation ? 0 : -1}
      >
        <div
          style={{
            height:
              direction === "vertical"
                ? `${virtualizer.getTotalSize()}px`
                : "100%",
            width:
              direction === "horizontal"
                ? `${virtualizer.getTotalSize()}px`
                : "100%",
            position: "relative",
          }}
        >
          {virtualItems.map(renderVirtualItem)}
        </div>

        {/* Screen reader announcements */}
        {enableKeyboardNavigation && keyboardState.isNavigating && (
          <div className={styles.srOnly} aria-live="polite">
            {keyboardState.focusedIndex >= 0 &&
            keyboardState.focusedIndex < items.length
              ? `Item ${keyboardState.focusedIndex + 1} of ${
                  items.length
                } focused`
              : "Navigation cleared"}
          </div>
        )}
      </div>
    );
  }
);

VirtualInfiniteScroll.displayName = "VirtualInfiniteScroll";

export default VirtualInfiniteScroll;
